import { useCallback, useState } from 'react';
import type { DeviceDto } from '@gc/shared';

import { api } from '@/lib/api';

/**
 * Edit layout — arranging the board so the tiles sit where the machines do.
 *
 * The order lives in `Device.sortOrder`, which the board already sorts by; this
 * hook owns the DRAFT of that order while the mode is open. Nothing is written
 * until Save, so a mis-drag costs a Cancel rather than a trip back through the
 * grid, and the running board is never renumbered under a cashier mid-shift.
 *
 * Deliberately no drag-and-drop library: this is one list, and the app has
 * pulled in no dnd dependency for anything else.
 */
export function moveWithin<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) {
    return [...items];
  }
  const next = [...items];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as T);
  return next;
}

export interface LayoutEditor {
  editing: boolean;
  /** The draft order while editing; null when the board shows server order. */
  draft: DeviceDto[] | null;
  dragging: string | null;
  saving: boolean;
  error: string | null;
  start: (devices: readonly DeviceDto[]) => void;
  cancel: () => void;
  save: () => Promise<boolean>;
  onDragStart: (deviceId: string) => void;
  onDragEnd: () => void;
  /** Reorders live, as the pointer passes over a tile. */
  onDragOver: (deviceId: string) => void;
  /** Keyboard path — a board arranged only by dragging is unusable without a mouse. */
  nudge: (deviceId: string, delta: number) => void;
}

export function useLayoutEditor(onSaved: () => void): LayoutEditor {
  const [draft, setDraft] = useState<DeviceDto[] | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback((devices: readonly DeviceDto[]) => {
    setDraft([...devices]);
    setError(null);
  }, []);

  const cancel = useCallback(() => {
    setDraft(null);
    setDragging(null);
    setError(null);
  }, []);

  const reorder = useCallback((deviceId: string, target: (index: number) => number) => {
    setDraft((current) => {
      if (!current) return current;
      const from = current.findIndex((device) => device.id === deviceId);
      if (from < 0) return current;
      return moveWithin(current, from, target(from));
    });
  }, []);

  const onDragOver = useCallback((deviceId: string) => {
    setDragging((held) => {
      if (!held || held === deviceId) return held;
      setDraft((current) => {
        if (!current) return current;
        const from = current.findIndex((device) => device.id === held);
        const to = current.findIndex((device) => device.id === deviceId);
        if (from < 0 || to < 0) return current;
        return moveWithin(current, from, to);
      });
      return held;
    });
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!draft) return false;
    setSaving(true);
    setError(null);
    try {
      await api.post('/devices/reorder', { deviceIds: draft.map((device) => device.id) });
      setDraft(null);
      setDragging(null);
      onSaved();
      return true;
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save the layout.');
      return false;
    } finally {
      setSaving(false);
    }
  }, [draft, onSaved]);

  return {
    editing: draft !== null,
    draft,
    dragging,
    saving,
    error,
    start,
    cancel,
    save,
    onDragStart: setDragging,
    onDragEnd: () => setDragging(null),
    onDragOver,
    nudge: (deviceId, delta) => reorder(deviceId, (from) => from + delta),
  };
}
